import { z } from 'zod';
import { ENABLE_EVENT_BOOKING_UI } from '@/lib/activities/featureFlags';

export const eventFormSchema = z.object({
  title: z.string().min(1, 'Titel krävs').max(200, 'Titel får max vara 200 tecken'),
  event_type: z.enum(['course', 'event', 'workshop', 'dinner', 'outing'], {
    required_error: 'Välj typ av aktivitet',
  }),
  timing_mode: z.enum(['date_time', 'open', 'anytime', 'season']).default('date_time'),
  selected_seasons: z.array(z.string()).default([]),
  description: z.string().optional(),
  // Simplified date/time fields – optional for outings
  event_date: z.string().optional().default(''),
  start_time: z.string().optional().default(''),
  end_time: z.string().optional().default(''),
  is_multi_day: z.boolean().default(false),
  end_date: z.string().optional(),
  ends_next_day: z.boolean().default(false),
  // Legacy fields for compatibility
  start_date: z.string().optional(),
  // For recurring events
  is_recurring: z.boolean().default(false),
  selected_dates: z.array(z.string()).default([]),
  use_producer_address: z.boolean().default(true),
  custom_address: z.string().optional(),
  price_sek: z.number().min(0, 'Pris måste vara 0 eller högre').default(0),
  is_free: z.boolean().default(true),
  max_spots: z.number().min(1, 'Minst 1 plats krävs').default(10),
  booking_deadline_hours: z.number().min(0, 'Bokningsstopp måste vara 0 eller högre').default(24),
  status: z.enum(['draft', 'public']).default('draft'),
  // Booking intent
  booking_mode: z.enum(['none', 'optional', 'required']).default('none'),
  // Audience visibility
  audience: z.enum(['public', 'authenticated']).default('public'),
  // Outing-specific
  outing_tags: z.array(z.string()).max(5, 'Max 5 taggar').default([]),
  learning_points: z.array(z.string()).default([]),
  includes_farm_sale: z.boolean().default(false),
  includes_farm_sale_alcohol: z.boolean().default(false),
}).refine((data) => {
  if (data.event_type === 'outing' || data.timing_mode !== 'date_time') return true;
  if (!data.is_recurring && !data.event_date) {
    return false;
  }
  return true;
}, {
  message: 'Datum krävs',
  path: ['event_date'],
}).refine((data) => {
  if (data.event_type === 'outing' || data.timing_mode !== 'date_time') return true;
  if (!data.start_time) {
    return false;
  }
  return true;
}, {
  message: 'Starttid krävs',
  path: ['start_time'],
}).refine((data) => {
  if (data.timing_mode !== 'open') return true;
  if (data.end_date && !data.start_date) {
    return false;
  }
  return true;
}, {
  message: 'Välj Från datum först om du vill använda Till datum',
  path: ['end_date'],
}).refine((data) => {
  if (data.event_type === 'outing' || data.timing_mode !== 'date_time') return true;
  // Only validate end > start when end_time is explicitly provided
  if (!data.is_recurring && data.event_date && data.start_time && data.end_time) {
    const startDate = data.event_date;
    let endDate = data.event_date;
    
    if (data.is_multi_day && data.end_date) {
      endDate = data.end_date;
    } else if (data.ends_next_day) {
      const d = new Date(data.event_date);
      d.setDate(d.getDate() + 1);
      endDate = d.toISOString().split('T')[0];
    }
    
    const startDateTime = new Date(`${startDate}T${data.start_time}`);
    const endDateTime = new Date(`${endDate}T${data.end_time}`);
    return endDateTime > startDateTime;
  }
  return true;
}, {
  message: 'Sluttid måste vara efter starttid',
  path: ['end_time'],
}).refine((data) => {
  if (ENABLE_EVENT_BOOKING_UI && !data.is_free && data.price_sek <= 0) {
    return false;
  }
  return true;
}, {
  message: 'Ange ett pris eller markera aktiviteten som gratis',
  path: ['price_sek'],
}).refine((data) => {
  if (data.event_type === 'outing' || data.timing_mode !== 'date_time') return true;
  if (data.is_recurring && data.selected_dates.length === 0) {
    return false;
  }
  return true;
}, {
  message: 'Välj minst ett datum för återkommande aktivitet',
  path: ['selected_dates'],
}).refine((data) => {
  if (data.event_type === 'outing' || data.timing_mode !== 'date_time') return true;
  if (data.is_multi_day && !data.end_date) {
    return false;
  }
  return true;
}, {
  message: 'Slutdatum krävs för flerdagarsaktivitet',
  path: ['end_date'],
});

export type EventFormData = z.infer<typeof eventFormSchema>;

export const defaultEventFormValues: EventFormData = {
  title: '',
  event_type: 'event',
  timing_mode: 'date_time',
  selected_seasons: [],
  description: '',
  event_date: '',
  start_time: '',
  end_time: '',
  is_multi_day: false,
  end_date: '',
  ends_next_day: false,
  start_date: '',
  is_recurring: false,
  selected_dates: [],
  use_producer_address: true,
  custom_address: '',
  price_sek: 0,
  is_free: true,
  max_spots: 10,
  booking_deadline_hours: 24,
  status: 'draft',
  booking_mode: 'none',
  audience: 'public',
  outing_tags: [],
  learning_points: [],
  includes_farm_sale: false,
  includes_farm_sale_alcohol: false,
};
